/**
 * bodyMetricsStrings.ts — тексты ВЫЧИСЛЯЕМЫХ значений для посетителя воронки
 * (решение владельца №5): слова уровня и категории ИМТ, единица веса и
 * тексты подсказки под вводом роста/веса (spec/new-blocks/bmi-hint-card.md §4).
 *
 * Язык — язык ВОРОНКИ, не кабинета: поэтому это не кабинетный словарь
 * (`defineDictionary` и реестр `i18n/dictionaries` — только для кабинета,
 * сторож WEB-1874), а словарь плеера рядом с его рантаймом (`player/`), как
 * `handoffSuccessStrings.ts` у страниц покупателя: ru + en, неизвестный язык
 * → en. Выбор — по `system.locale` контекста переменных
 * (`pickBodyMetricsStrings`). Обе половины типизированы одним интерфейсом:
 * пропуск ключа — ошибка компиляции.
 *
 * Английские тексты — дословно эталон (`slimkit-research/src/main.js`, ключи
 * `warningBMIMessage_*`, `wellnessProfile_13..15`). Остальные 28 языков
 * воронки получают английский — перевод отдельной задачей (как у
 * `publicQuizStrings` нужен пакет на каждый язык).
 *
 * Подстановки в текстах подсказки — те же имена, что у вычисляемых
 * переменных: {{bmi}}, {{weightLossPercent}}, {{normalWeightMin}},
 * {{normalWeightMax}}. Разметка — только `<b>` (выделение у эталона).
 */
/** Английская половина: дословно эталон. */
const EN = {
  levelLow: 'low',
  levelNormal: 'normal',
  levelHigh: 'high',
  categoryUnderweight: 'underweight',
  categoryNormal: 'normal',
  categoryOverweight: 'overweight',
  categoryObese: 'obese',
  unitKg: 'kg',
  unitLb: 'lb',
  // ── Подсказка под вводом (bmi-hint-card.md §4) ─────────────────────────
  hintHeightIdleTitle: 'Calculating your BMI',
  hintHeightIdleText:
    'Body mass index (BMI) is a metric of body fat percentage commonly used to estimate risk levels of potential health problems.',
  hintHeightInvalidTitle: 'Please double-check and enter valid height.',
  hintWeightTooLowTitle: 'Your weight is too low for this program.',
  hintWeightTooHighTitle:
    'Your weight is too high to work out with this program.',
  hintUnderweightTitle:
    'Your BMI is {{bmi}} which is considered <b>underweight</b>',
  hintUnderweightText:
    'You have some work ahead of you, but it’s great that you’re taking this first step. We’ll use your BMI to create a program just for you.',
  hintNormalTitle: 'Your BMI is {{bmi}} which is considered <b>normal</b>',
  hintNormalText:
    'You’re starting from a great place! Now we’ll use your BMI to create a program tailored to your needs.',
  hintOverweightTitle:
    'Your BMI is {{bmi}} which is considered <b>overweight</b>',
  hintOverweightText:
    'You have some work ahead of you, but it’s great that you’re taking this first step. We’ll use your BMI to create a weight loss program just for you.',
  hintObeseTitle: 'Your BMI is {{bmi}} which is considered <b>obese</b>',
  hintObeseText:
    "There's a lot you could gain by losing a little weight. We’ll use your BMI to create the weight loss program you need.",
  hintGoalBelowNormTitle: 'Uh-oh! Low weight alert!',
  hintGoalBelowNormText:
    'A normal weight range for your height is between {{normalWeightMin}} and {{normalWeightMax}}. Any weight below {{normalWeightMin}} is classified as underweight and is not recommended by World Health Organization.',
  hintGoalNotLessTitle:
    'Your target weight should be less than your current weight.',
  hintGoalNotLessText:
    'Please double check. You might have used metric system instead of imperial. You can change preferred unit system at the top of this page.',
  hintGoalOkTitle:
    'Get moving: <b>lose {{weightLossPercent}}%</b> of your weight',
  hintGoalOkText:
    'Working out just 5 minutes per day can significantly improve your overall well-being.',
};

export type BodyMetricsStrings = Readonly<Record<keyof typeof EN, string>>;

const RU: BodyMetricsStrings = {
  levelLow: 'низкий',
  levelNormal: 'нормальный',
  levelHigh: 'высокий',
  categoryUnderweight: 'недостаточный вес',
  categoryNormal: 'норма',
  categoryOverweight: 'избыточный вес',
  categoryObese: 'ожирение',
  unitKg: 'кг',
  unitLb: 'фунт.',
  hintHeightIdleTitle: 'Считаем ваш ИМТ',
  hintHeightIdleText:
    'Индекс массы тела (ИМТ) — показатель доли жира в теле; по нему обычно оценивают риск проблем со здоровьем.',
  hintHeightInvalidTitle: 'Проверьте рост и введите корректное значение.',
  hintWeightTooLowTitle: 'Для этой программы ваш вес слишком мал.',
  hintWeightTooHighTitle:
    'Для тренировок по этой программе ваш вес слишком велик.',
  hintUnderweightTitle: 'Ваш ИМТ {{bmi}} — это <b>недостаточный вес</b>',
  hintUnderweightText:
    'Впереди работа, но здорово, что вы делаете первый шаг. По вашему ИМТ мы составим программу именно для вас.',
  hintNormalTitle: 'Ваш ИМТ {{bmi}} — это <b>норма</b>',
  hintNormalText:
    'Отличная отправная точка! По вашему ИМТ мы составим программу под ваши задачи.',
  hintOverweightTitle: 'Ваш ИМТ {{bmi}} — это <b>избыточный вес</b>',
  hintOverweightText:
    'Впереди работа, но здорово, что вы делаете первый шаг. По вашему ИМТ мы составим программу снижения веса именно для вас.',
  hintObeseTitle: 'Ваш ИМТ {{bmi}} — это <b>ожирение</b>',
  hintObeseText:
    'Даже небольшое снижение веса даст многое. По вашему ИМТ мы составим нужную вам программу снижения веса.',
  hintGoalBelowNormTitle: 'Осторожно: слишком низкий вес!',
  hintGoalBelowNormText:
    'Нормальный вес для вашего роста — от {{normalWeightMin}} до {{normalWeightMax}}. Вес ниже {{normalWeightMin}} считается недостаточным, и Всемирная организация здравоохранения его не рекомендует.',
  hintGoalNotLessTitle: 'Желаемый вес должен быть меньше текущего.',
  hintGoalNotLessText:
    'Проверьте ещё раз: возможно, вы ввели вес не в тех единицах. Единицы можно переключить вверху страницы.',
  hintGoalOkTitle: 'Вперёд: <b>минус {{weightLossPercent}}%</b> веса',
  hintGoalOkText:
    'Даже 5 минут тренировок в день заметно улучшают самочувствие.',
};

export const bodyMetricsStrings: Readonly<{
  en: BodyMetricsStrings;
  ru: BodyMetricsStrings;
}> = {
  en: EN,
  ru: RU,
};

/** Тексты по языку воронки: русский — свой, остальные — английский. */
export function pickBodyMetricsStrings(
  locale: string | undefined | null,
): BodyMetricsStrings {
  const tag = (locale ?? '').trim().toLowerCase();
  return tag === 'ru' || tag.startsWith('ru-')
    ? bodyMetricsStrings.ru
    : bodyMetricsStrings.en;
}
